import { ConnectionListResult, FeatureFlagsContext, GridSortProps, MaxCountsResultData, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceUpstreamVirtualConnectionTablesQuery,
  DatasourceUpstreamVirtualConnectionTablesQueryVariables,
} from '../../../gen-src/queries/DatasourceUpstreamVirtualConnectionTablesQuery';
import {
  UpstreamVirtualConnectionTablesFilteredByFieldQuery,
  UpstreamVirtualConnectionTablesFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/UpstreamVirtualConnectionTablesFilteredByFieldQuery';
import { VirtualConnectionTablesDisplayGridRowDataFragment } from '../../../gen-src/queries/VirtualConnectionTablesDisplayGridRowDataFragment';
import { VirtualConnectionTableSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceUpstreamVirtualConnectionTablesQueryGql } from '../../queries/DatasourceUpstreamVirtualConnectionTablesQueryGql';
import { UpstreamVirtualConnectionTablesFilteredByFieldQueryGql } from '../../queries/UpstreamVirtualConnectionTablesFilteredByFieldQueryGql';
import { VirtualConnectionTablesDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionTablesDisplayGrid';
import { VirtualConnectionTablesValidSortFields } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { getRowDataWithMaxCountsForVirtualConnectionTable } from '../table/functions/VirtualConnectionTableRowDataFunctions';
import { VirtualConnectionTablesDisplayGridRowData } from "../table/types/VirtualConnectionTablesDisplayGridRowData";

type QueryVariables = DatasourceUpstreamVirtualConnectionTablesQueryVariables;
type QueryResults = DatasourceUpstreamVirtualConnectionTablesQuery;
type QueryResultListNode = VirtualConnectionTablesDisplayGridRowDataFragment;
type GridRowData = VirtualConnectionTablesDisplayGridRowData;

type QueryVariablesWithFilter = UpstreamVirtualConnectionTablesFilteredByFieldQueryVariables;
type QueryResultsWithFilter = UpstreamVirtualConnectionTablesFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0]
    && resultData.publishedDatasources[0].upstreamVirtualConnectionTablesConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter): ConnectionListResult<QueryResultListNode> | null {
  return (
    resultData &&
    resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] &&
    resultData.fieldsConnection.nodes[0].upstreamVirtualConnectionTablesConnection
  );
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes && resultData.fieldsConnection.nodes[0];
}

function getRowDataForVirtualConnectionTable(
  node: QueryResultListNode,
  resultData: QueryResults,
): GridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(node, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionTableForFilteredQuery(
  node: QueryResultListNode,
  resultData: QueryResultsWithFilter,
): GridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(node, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatasourceUpstreamVirtualConnectionTablesQueryGridProps = GridSortProps<VirtualConnectionTablesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredFieldId: string | null;
  readonly rowCount: number | null;
};

export function DatasourceUpstreamVirtualConnectionTablesQueryGrid(props: DatasourceUpstreamVirtualConnectionTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamVirtualConnectionTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredVirtualConnectionTablesLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={UpstreamVirtualConnectionTablesFilteredByFieldQueryGql}
      partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as VirtualConnectionTableSortOrder, publishedConnectionsLineageEnabled }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionTableForFilteredQuery}
    >
      {({ rowStore }) => (
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={DatasourceUpstreamVirtualConnectionTablesQueryGql}
      partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as VirtualConnectionTableSortOrder, publishedConnectionsLineageEnabled }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnectionTable}
    >
      {({ rowStore }) => (
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  );
}
